(() => {
  if (customElements.get('olea-pro')) return;

  const fields = new Map([
    ['name', 200],
    ['company', 200],
    ['email', 254],
    ['pieces', 500],
    ['project', 20]
  ]);
  const projectTypes = new Set(['hotel', 'restaurant', 'office', 'architect']);
  const retention = 30 * 60 * 1000;

  class OleaPro extends HTMLElement {
    connectedCallback() {
      if (this.controller) return;
      this.form = this.querySelector('form');
      this.quote = this.querySelector('#ProQuote');
      if (!this.form || !this.quote) return;
      this.controller = new AbortController();
      const { signal } = this.controller;
      const customer = /^\d+$/.test(this.dataset.proCustomerId || '') ? this.dataset.proCustomerId : 'guest';
      this.storageKey = `olea:pro-contact:${location.pathname}:${this.dataset.proSection || 'main'}:${customer}`;
      this.serverStatus = this.querySelector('[data-pro-submit-status]');
      this.submitting = false;
      // A dynamically connected section may arrive before the event bridge.
      // Replay only the native server status when that bridge becomes ready.
      const settleServerSubmission = () => {
        if (this.serverStatus?.dataset.proSubmitStatus === 'success') {
          window.OleaKlaviyoEvents?.confirmProSubmission(this.form, this.dataset.proSection, this.serverStatus.dataset.proSubmitEmail);
        } else if (this.serverStatus?.dataset.proSubmitStatus === 'error') window.OleaKlaviyoEvents?.cancelProSubmission();
      };
      document.addEventListener('olea:klaviyo-events-ready', settleServerSubmission, { signal });
      if (this.serverStatus?.dataset.proSubmitStatus === 'success') {
        settleServerSubmission();
        this.clearSaved();
      } else {
        settleServerSubmission();
        this.restoreSaved();
      }
      this.form.addEventListener('submit', event => this.onSubmit(event), { signal });
      document.addEventListener('click', event => this.onProjectClick(event), { signal });
      window.addEventListener('pageshow', () => this.resetSubmit(), { signal });
      if (this.serverStatus) {
        this.statusFrame = requestAnimationFrame(() => this.serverStatus?.focus({ preventScroll: true }));
      }
    }

    disconnectedCallback() {
      this.controller?.abort();
      this.controller = null;
      cancelAnimationFrame(this.statusFrame);
      this.resetSubmit();
    }

    input(name) {
      return this.form.querySelector(`[name="contact[${name}]"]`);
    }

    radios() {
      return Array.from(this.form.querySelectorAll('input[type="radio"][name="contact[project]"]'));
    }

    clearSaved() {
      try { sessionStorage.removeItem(this.storageKey); } catch {}
    }

    saveFields() {
      if (this.dataset.preserveFields !== 'true') return;
      const values = {};
      fields.forEach((max, name) => {
        const input = name === 'project' ? this.radios().find(radio => radio.checked) : this.input(name);
        if (input && (name !== 'project' || projectTypes.has(input.value))) values[name] = String(input.value).slice(0, max);
      });
      try { sessionStorage.setItem(this.storageKey, JSON.stringify({ time: Date.now(), values })); } catch {}
    }

    restoreSaved() {
      if (this.dataset.preserveFields !== 'true') {
        this.clearSaved();
        return;
      }
      let saved;
      try { saved = JSON.parse(sessionStorage.getItem(this.storageKey) || 'null'); } catch { return; }
      if (!saved) return;
      const age = Date.now() - saved.time;
      if (!Number.isFinite(age) || age < 0 || age > retention || !saved.values || typeof saved.values !== 'object') {
        this.clearSaved();
        return;
      }
      fields.forEach((max, name) => {
        const value = saved.values[name];
        if (typeof value !== 'string' || value.length > max) return;
        if (name === 'project') {
          this.selectProject(value);
          return;
        }
        const input = this.input(name);
        if (!input) return;
        const serverEcho = this.serverStatus?.dataset.proSubmitStatus === 'error' && (name === 'name' || name === 'email') && input.value.trim();
        if (!serverEcho) input.value = value;
      });
    }

    selectProject(value) {
      if (!projectTypes.has(value)) return false;
      const radios = this.radios();
      const selected = radios.find(radio => radio.value === value);
      if (!selected) return false;
      radios.forEach(radio => { radio.checked = radio === selected; });
      selected.dispatchEvent(new Event('change', { bubbles: true }));
      return true;
    }

    onProjectClick(event) {
      if (event.defaultPrevented || event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
      const link = event.target.closest?.('a[data-pro-project]');
      if (!link || link.hasAttribute('download') || (link.target && link.target !== '_self')) return;
      let url;
      try { url = new URL(link.href, location.href); } catch { return; }
      const current = new URL(location.href);
      if (url.origin !== current.origin || url.pathname !== current.pathname || url.search !== current.search || url.hash !== '#ProQuote') return;
      if (!this.selectProject(link.dataset.proProject)) return;
      event.preventDefault();
      const smooth = document.body.classList.contains('animations-enabled') && !window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      this.quote.focus({ preventScroll: true });
      this.quote.scrollIntoView({ behavior: smooth ? 'smooth' : 'auto', block: 'start' });
    }

    onSubmit(event) {
      if (event.defaultPrevented) return;
      if (this.submitting || !this.form.checkValidity()) {
        event.preventDefault();
        return;
      }
      this.saveFields();
      const trackingRequest = window.OleaKlaviyoEvents?.prepareProSubmission(this.form, this.dataset.proSection);
      this.submitting = true;
      this.form.setAttribute('aria-busy', 'true');
      this.submitButton = event.submitter || this.form.querySelector('button[type="submit"]');
      if (this.submitButton) this.submitButton.disabled = true;
      queueMicrotask(() => { if (event.defaultPrevented) { this.resetSubmit(); if (trackingRequest) window.OleaKlaviyoEvents?.cancelProSubmission(trackingRequest); } });
    }

    resetSubmit() {
      this.submitting = false;
      this.form?.removeAttribute('aria-busy');
      if (this.submitButton) this.submitButton.disabled = false;
    }
  }

  customElements.define('olea-pro', OleaPro);
})();
